import React, { useState } from 'react';
import {
  Button,
  SpaceBetween,
  Box,
  Modal,
  Input,
  ButtonDropdown,
  FormField,
  Form,
  Alert,
} from '@cloudscape-design/components';
import { UserViewPreferences } from '@amzn/controllershiphub-typescript-client';
import { FilterSet } from 'src/common/FilterSet';
import './filterstyles.scss';

interface AppliedFiltersProps {
  activeFilters: Record<string, string[]>;
  onSaveFilterView: (filterSetName: string) => Promise<string>;
  onShareView: (viewName: string, aliases: string, description: string) => Promise<void>;
  hasActiveFilters: boolean;
  savedFilterViews: Record<string, UserViewPreferences>;
  onFilterViewSelect: (viewName: string) => void;
  currentViewName: string;
  loading?: boolean;
  isSharing?: boolean;
  shareError: string | null;
  clearShareError: () => void;
  onLoadMore?: () => void;
  hasMoreData?: boolean;
  isLoadingMore?: boolean;
  onExportData?: () => void;
  controlId?: string;
  dataType?: string;
}

export const AppliedFilters: React.FC<AppliedFiltersProps> = ({
  activeFilters,
  onSaveFilterView,
  onShareView,
  hasActiveFilters,
  savedFilterViews,
  onFilterViewSelect,
  currentViewName,
  loading = false,
  isSharing = false,
  shareError,
  clearShareError,
  onLoadMore,
  hasMoreData,
  isLoadingMore,
  onExportData,
  controlId,
  dataType,
}) => {
  const [expandedFilters, setExpandedFilters] = useState<Record<string, boolean>>({});
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [isSavingView, setIsSavingView] = useState(false);
  const [shareAliases, setShareAliases] = useState('');
  const [shareDescription, setShareDescription] = useState('');

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setShareAliases('');
    setShareDescription('');
    setNewViewName('');
    clearShareError();
  };

  const handleOpenShareModal = () => {
    if (currentViewName !== 'Default View') {
      setNewViewName(currentViewName);
    } else {
      setNewViewName('');
    }
    setIsShareModalOpen(true);
  };

  const handleSaveView = async () => {
    setIsSavingView(true);
    try {
      await onSaveFilterView(newViewName);
      setIsSaveModalOpen(false);
      setNewViewName('');
    } catch (error) {
      console.error('Error saving filter view:', error);
    } finally {
      setIsSavingView(false);
    }
  };

  const handleShareView = async () => {
    try {
      if (onShareView) {
        await onShareView(newViewName, shareAliases, shareDescription);
        handleCloseShareModal();
      }
    } catch (error) {
      console.error('Error sharing view:', error);
    }
  };

  const processFilterValue = (filterConfig: any): string[] => {
    if (!filterConfig) return [];

    // for array of values
    if (Array.isArray(filterConfig)) {
      return filterConfig;
    }

    // Handle AG Grid filter values
    if (filterConfig.values) {
      return filterConfig.values;
    }

    // Handle single filter value
    if (filterConfig.filter) {
      return [filterConfig.filter];
    }
    return [];
  };

  const processedFilters = Object.entries(activeFilters)
    .map(([columnName, filterConfig]) => ({
      columnName,
      values: processFilterValue(filterConfig),
    }))
    .filter((filter) => filter.values.length > 0);

  const toggleFilterExpansion = (filterName: string) => {
    setExpandedFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const dropdownItems = [
    {
      text: 'Save as Current View',
      id: 'save',
      disabled: !hasActiveFilters,
    },
    {
      text: 'Share Current View',
      id: 'share',
      disabled: !hasActiveFilters,
    },
    ...(dataType === 'PO' && onExportData
      ? [
          {
            text: 'Export Data',
            id: 'export',
            disabled: false, // Always enabled for PO data export
          },
        ]
      : []),
  ];

  const handleDropdownClick = ({ detail }: { detail: { id: string } }) => {
    switch (detail.id) {
      case 'save':
        setIsSaveModalOpen(true);
        break;
      case 'share':
        handleOpenShareModal();
        break;
      case 'export':
        onExportData?.();
        break;
    }
  };

  return (
    <div className="applied-filters">
      <div className="applied-filters__header">
        <div className="applied-filters__header-content">
          <h4 style={{ fontWeight: 700 }}>Applied Filters</h4>
          <SpaceBetween direction="horizontal" size="xs">
            <FilterSet
              filterViews={savedFilterViews}
              onFilterSetSelect={onFilterViewSelect}
              loading={loading}
              selectedView={currentViewName}
            />
            {hasMoreData && onLoadMore && (
              <Button onClick={onLoadMore} loading={isLoadingMore} disabled={isLoadingMore}>
                Load More
              </Button>
            )}
            <ButtonDropdown items={dropdownItems} onItemClick={handleDropdownClick}>
              Actions
            </ButtonDropdown>
          </SpaceBetween>
        </div>

        {processedFilters.length > 0 ? (
          <div className="applied-filters__filters">
            {processedFilters.map((filter) => {
              const isExpanded = expandedFilters[filter.columnName];
              const visibleValues = isExpanded ? filter.values : filter.values.slice(0, 2);
              const hasMoreValues = filter.values.length > 2;

              return (
                <div key={filter.columnName} className="applied-filters__filters-item">
                  <span className="applied-filters__filters-name">{filter.columnName}</span>
                  <span style={{ fontWeight: 'normal' }}>Includes:</span>
                  <div className="applied-filters__filters-values">
                    {visibleValues.map((value, index) => (
                      <React.Fragment key={value}>
                        <span className="applied-filters__filters-value">"{value}"</span>
                        {index < visibleValues.length - 1 && <span>,</span>}
                      </React.Fragment>
                    ))}
                    {hasMoreValues && (
                      <Button
                        variant="link"
                        onClick={() => toggleFilterExpansion(filter.columnName)}
                      >
                        {isExpanded ? 'Show less' : `+${filter.values.length - 2} more`}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>No Applied Filters</div>
        )}
      </div>
      {controlId === 'grc18956' && (
        <Alert data-testid="grc18956-export-data-alert">
          Below is only an excerpt of the data query output, please use the “Export Data” function
          under “Actions” to download the full report output.
        </Alert>
      )}
      <Modal
        visible={isSaveModalOpen}
        onDismiss={() => setIsSaveModalOpen(false)}
        header="Save Filter Set"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setIsSaveModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveView}
                loading={isSavingView}
                disabled={!newViewName.trim() || isSavingView || !hasActiveFilters}
              >
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Input
            value={newViewName}
            onChange={({ detail }) => setNewViewName(detail.value)}
            placeholder="Enter filter set name"
          />
        </SpaceBetween>
      </Modal>
      <Modal
        visible={isShareModalOpen}
        onDismiss={handleCloseShareModal}
        header={
          <div>
            <h4 style={{ marginBottom: '4px' }}>Share View</h4>
            <Box color="text-status-info" fontWeight="bold" fontSize="body-m">
              {currentViewName === 'Default View'
                ? ''
                : 'You are sharing the current selected view'}
            </Box>
          </div>
        }
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xxs">
              <Button variant="link" onClick={handleCloseShareModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleShareView}
                loading={isSharing}
                disabled={!shareAliases.trim() || !newViewName.trim() || isSharing}
              >
                Share
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {shareError && (
          <Box fontWeight={'bold'} color="text-status-error" margin={{ bottom: 's' }}>
            {shareError}
          </Box>
        )}
        <Form>
          <SpaceBetween direction="vertical" size="s">
            <FormField
              label={
                <>
                  View name<span style={{ color: '#D91515' }}> *</span>
                </>
              }
            >
              <Input
                value={newViewName}
                onChange={({ detail }) => setNewViewName(detail.value)}
                placeholder="Enter view name"
              />
            </FormField>

            <FormField
              label={
                <>
                  Aliases to share (Max 10)<span style={{ color: '#D91515' }}> *</span>
                </>
              }
            >
              <Input
                value={shareAliases}
                onChange={({ detail }) => setShareAliases(detail.value)}
                placeholder="e.g., alias1, alias2, alias3"
              />
            </FormField>

            <FormField label="Additional notes">
              <Input
                value={shareDescription}
                onChange={({ detail }) => setShareDescription(detail.value)}
                placeholder="Optional notes"
              />
            </FormField>
            <Box color="text-body-secondary" fontSize="body-s">
              Fields marked with an asterisk (<span style={{ color: '#D91515' }}>*</span>) are
              required.
            </Box>
          </SpaceBetween>
        </Form>
      </Modal>
    </div>
  );
};
